import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, CreditCard, History, BarChart3,
  Users, Settings, LogOut, Menu, Zap, ChevronDown, Plus,
  Repeat, HelpCircle, FileText
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const navItems = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { path: '/payment', labelKey: 'nav.payment', icon: CreditCard },
  { path: '/templates', labelKey: 'nav.templates', icon: Repeat },
  { path: '/transactions', labelKey: 'nav.transactions', icon: History },
  { path: '/analytics', labelKey: 'nav.analytics', icon: BarChart3 },
  { path: '/reports', labelKey: 'nav.reports', icon: FileText },
  { path: '/staff', labelKey: 'nav.staff', icon: Users },
  { path: '/settings', labelKey: 'nav.settings', icon: Settings },
  { path: '/support', labelKey: 'nav.support', icon: HelpCircle },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [activeStore, setActiveStore] = useState(null);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const [storesRes, storeRes] = await Promise.all([
          api.get('/stores/list'),
          api.get('/store/profile')
        ]);
        setStores(storesRes.data.stores);
        setActiveStore(storeRes.data);
      } catch {}
    };
    fetchStores();
  }, []);

  const handleSwitchStore = async (storeId) => {
    try {
      await api.post(`/stores/switch/${storeId}`);
      const storeRes = await api.get('/store/profile');
      setActiveStore(storeRes.data);
      toast.success(`Switched to ${storeRes.data.name}`);
      window.location.reload();
    } catch (err) {
      toast.error('Failed to switch store');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavContent = ({ onItemClick }) => (
    <div className="flex flex-col h-full">
      <div className="p-5 flex items-center gap-2.5">
        <Zap className="w-7 h-7 text-orange-500 flex-shrink-0" />
        <span className="text-xl font-extrabold tracking-tighter text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
          EmergencyPOS
        </span>
      </div>

      {/* Store Switcher */}
      {stores.length > 0 && (
        <div className="px-3 pb-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center justify-between px-3 py-2 text-sm bg-muted hover:bg-muted/80 transition-colors text-left"
                data-testid="store-switcher-btn"
              >
                <div className="truncate">
                  <div className="font-medium text-foreground truncate text-xs">{activeStore?.name || 'Select Store'}</div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 ml-2" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              {stores.map((s) => (
                <DropdownMenuItem
                  key={s.store_id}
                  onClick={() => handleSwitchStore(s.store_id)}
                  className={s.store_id === activeStore?.store_id ? 'bg-muted font-medium' : ''}
                  data-testid={`switch-store-${s.store_id}`}
                >
                  {s.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { onItemClick(); navigate('/settings'); }} data-testid="create-new-store-btn">
                <Plus className="w-4 h-4 mr-2" /> New Store
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <Separator />

      <nav className="flex-1 p-2 space-y-0.5 mt-2" data-testid="main-navigation">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onItemClick}
            data-testid={`nav-${item.path.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary/10 text-primary nav-item-active'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      <Separator />

      <div className="p-4">
        <div className="text-sm font-medium text-foreground truncate" data-testid="user-name">
          {user?.name}
        </div>
        <div className="text-xs text-muted-foreground truncate" data-testid="user-email">
          {user?.email}
        </div>
        <div className="text-xs text-muted-foreground/70 mt-0.5 uppercase tracking-wider font-semibold">
          {user?.role}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 justify-start text-muted-foreground hover:text-foreground"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('nav.logout')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-56 lg:w-64 border-r border-border bg-card flex-col flex-shrink-0">
        <NavContent onItemClick={() => {}} />
      </aside>

      {/* Mobile + content */}
      <div className="flex flex-col flex-1 min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-card border-b border-border">
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-orange-500" />
            <span className="text-lg font-extrabold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>
              EmergencyPOS
            </span>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <NavContent onItemClick={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
